// Category icons + per-subtype questionnaires (required vs optional fields).
import {
  Coins, Briefcase, ShoppingBag, Home, ShieldAlert, Scale, Landmark,
} from "lucide-react";

export const CATEGORY_ICONS = {
  money_recovery: Coins,
  employment: Briefcase,
  consumer: ShoppingBag,
  property: Home,
  cyber: ShieldAlert,
  business_ip: Landmark,
  contract_general: Scale,
};

const t = (name, label, opts = {}) => ({ name, label, type: "text", required: false, ...opts });
const area = (name, label, opts = {}) => ({ name, label, type: "textarea", required: false, ...opts });

// field: { name, label, type, placeholder, help, example, required }
export const CLAIM_FIELDS = {
  // ---- Money Recovery ----
  unpaid_invoice: [
    t("invoice_amount", "Amount unpaid (₹)", { required: true, placeholder: "e.g. 85,000", help: "Total outstanding amount." }),
    area("invoice_details", "What was the invoice for?", { required: true, placeholder: "e.g. Invoice #INV-204 dated 2 Mar 2025 for website design", help: "Mention invoice number/date and the work or goods supplied." }),
    t("due_date", "Payment was due on", { placeholder: "e.g. 15 April 2025", help: "When the payment became due." }),
    area("followups", "Reminders you've already sent", { placeholder: "e.g. 3 emails and 2 calls, no response", help: "Optional — strengthens your notice." }),
  ],
  friendly_loan: [
    t("loan_amount", "Amount lent (₹)", { required: true, placeholder: "e.g. 1,50,000", help: "How much you gave." }),
    t("loan_date", "Date you gave the money", { required: true, placeholder: "e.g. 10 Jan 2025", help: "When the loan was given." }),
    t("how_given", "How was it given?", { placeholder: "e.g. Bank transfer / UPI / Cash", help: "Optional — payment mode." }),
    area("repayment_promise", "What was promised?", { placeholder: "e.g. Agreed to repay within 3 months over WhatsApp", help: "Any promise or agreement to repay." }),
  ],
  advance_refund: [
    t("advance_amount", "Advance paid (₹)", { required: true, placeholder: "e.g. 40,000", help: "The advance amount you want refunded." }),
    area("paid_for", "What was the advance for?", { required: true, placeholder: "e.g. Booking a banquet hall for 20 Dec, later cancelled", help: "What you paid for and why a refund is due." }),
    t("paid_date", "Date advance was paid", { placeholder: "e.g. 5 Nov 2025", help: "Optional." }),
    area("cancellation_reason", "Why is a refund owed?", { placeholder: "e.g. Service was cancelled by them / not provided", help: "Optional context." }),
  ],
  cheque_bounce: [
    t("cheque_amount", "Cheque amount (₹)", { required: true, placeholder: "e.g. 2,00,000", help: "Amount on the dishonoured cheque." }),
    t("cheque_number", "Cheque number", { required: true, placeholder: "e.g. 004512", help: "The cheque number." }),
    t("cheque_date", "Cheque date", { required: true, placeholder: "e.g. 12 May 2025", help: "Date written on the cheque." }),
    t("bank_name", "Bank name", { placeholder: "e.g. HDFC Bank", help: "Optional — drawee bank." }),
    area("underlying_reason", "Why was the cheque given to you?", { placeholder: "e.g. Repayment of a loan / against invoice", help: "Optional — the debt the cheque was for." }),
  ],

  // ---- Employment & Salary ----
  unpaid_salary: [
    t("amount_due", "Salary amount due (₹)", { required: true, placeholder: "e.g. 1,20,000", help: "Total unpaid salary." }),
    t("period_unpaid", "Period unpaid", { required: true, placeholder: "e.g. Feb–Mar 2025 (2 months)", help: "Which months are unpaid." }),
    t("designation", "Your designation", { placeholder: "e.g. Sales Executive", help: "Optional." }),
    area("followups", "What have you already done?", { placeholder: "e.g. Emailed HR twice, no reply", help: "Optional — prior follow-ups." }),
  ],
  fnf_delay: [
    t("last_working_day", "Last working day", { required: true, placeholder: "e.g. 31 Jan 2025", help: "When your employment ended." }),
    area("components_pending", "What's pending?", { required: true, placeholder: "e.g. Last salary, leave encashment, gratuity", help: "The F&F components not yet paid." }),
    t("fnf_amount", "Approx amount due (₹)", { placeholder: "e.g. 90,000", help: "Optional if known." }),
    t("resignation_date", "Resignation date", { placeholder: "e.g. 1 Jan 2025", help: "Optional." }),
  ],
  unpaid_bonus: [
    t("bonus_amount", "Amount due (₹)", { required: true, placeholder: "e.g. 50,000", help: "Bonus/incentive amount owed." }),
    t("bonus_type", "Type", { required: true, placeholder: "e.g. Annual bonus / Sales incentive / Commission", help: "What kind of payment it is." }),
    area("agreement", "How was it promised?", { placeholder: "e.g. Offer letter clause / email confirmation", help: "Optional — basis of the promise." }),
    t("period", "Period it relates to", { placeholder: "e.g. FY 2024-25", help: "Optional." }),
  ],
  wrongful_termination: [
    t("employment_period", "Employment period", { required: true, placeholder: "e.g. Jun 2022 – Mar 2025", help: "How long you worked there." }),
    t("termination_date", "Date of termination", { required: true, placeholder: "e.g. 15 Mar 2025", help: "When you were removed." }),
    area("why_wrongful", "Why was it wrongful?", { required: true, placeholder: "e.g. No notice, no reason, dues withheld", help: "Explain what made it unfair." }),
    t("dues_claimed", "Dues claimed (₹)", { placeholder: "e.g. 2 months salary + notice pay", help: "Optional." }),
  ],

  // ---- Consumer ----
  defective_product: [
    t("product_name", "Product", { required: true, placeholder: "e.g. Refrigerator, Model XZ-500", help: "What you bought." }),
    t("amount_paid", "Amount paid (₹)", { required: true, placeholder: "e.g. 42,000", help: "What you paid." }),
    area("issue", "What's the problem?", { required: true, placeholder: "e.g. Delivered damaged / wrong model / part missing", help: "Describe the defect or issue." }),
    t("relief_sought", "What do you want?", { required: true, placeholder: "e.g. Full refund / Replacement / Repair", help: "The outcome you're demanding." }),
    t("order_id", "Order ID", { placeholder: "e.g. AMZ-90231", help: "Optional." }),
    t("purchase_date", "Purchase date", { placeholder: "e.g. 2 Jan 2025", help: "Optional." }),
  ],
  order_not_delivered: [
    t("product_name", "What you ordered", { required: true, placeholder: "e.g. Bluetooth speaker", help: "The product/service ordered." }),
    t("amount_paid", "Amount paid (₹)", { required: true, placeholder: "e.g. 3,500", help: "What you paid." }),
    t("order_date", "Order date", { required: true, placeholder: "e.g. 10 Mar 2025", help: "When you placed the order." }),
    t("order_id", "Order ID", { placeholder: "e.g. FK-55120", help: "Optional." }),
    t("expected_date", "Promised delivery date", { placeholder: "e.g. 15 Mar 2025", help: "Optional." }),
  ],
  refund_not_received: [
    t("product_service", "Product / service", { required: true, placeholder: "e.g. Flight ticket / online course", help: "What the refund is for." }),
    t("amount", "Refund amount (₹)", { required: true, placeholder: "e.g. 12,000", help: "Amount owed to you." }),
    area("context", "What happened?", { required: true, placeholder: "e.g. Cancelled order on 3 May; refund promised in 7 days, still not received", help: "The background of the refund." }),
    t("refund_promised_date", "Refund promised by", { placeholder: "e.g. 10 May 2025", help: "Optional." }),
    t("order_id", "Order / booking ID", { placeholder: "e.g. MMT-77812", help: "Optional." }),
  ],
  service_deficiency: [
    t("service_name", "Service", { required: true, placeholder: "e.g. Home interior work", help: "The service you paid for." }),
    t("amount_paid", "Amount paid (₹)", { required: true, placeholder: "e.g. 1,80,000", help: "What you paid." }),
    area("deficiency", "What went wrong?", { required: true, placeholder: "e.g. Work left incomplete and defective after 3 months", help: "Describe the deficiency." }),
    t("relief_sought", "What do you want?", { required: true, placeholder: "e.g. Complete the work / refund ₹80,000", help: "The remedy you're demanding." }),
    t("provider", "Service provider", { placeholder: "e.g. XYZ Interiors", help: "Optional." }),
  ],

  // ---- Property & Rent ----
  rent_dispute: [
    area("property_address", "Property address", { required: true, placeholder: "Full address of the rented property", help: "The property in dispute." }),
    t("issue", "The issue", { required: true, placeholder: "e.g. Rent arrears / dispute over terms", help: "Core problem in a few words." }),
    area("details", "Full details", { required: true, placeholder: "Dates, amounts and what you want resolved", help: "Give the background." }),
    t("amount_or_period", "Amount / period involved", { placeholder: "e.g. ₹60,000 (3 months rent)", help: "Optional." }),
  ],
  tenant_not_vacating: [
    area("property_address", "Property address", { required: true, placeholder: "Full address of the property", help: "The property to be vacated." }),
    t("vacate_by_date", "Vacate by (date)", { required: true, placeholder: "e.g. 30 Jun 2025", help: "The date you want possession back." }),
    t("tenancy_since", "Tenant since", { placeholder: "e.g. Jan 2022", help: "Optional." }),
    area("details", "Details", { placeholder: "e.g. Lease ended, notice already given verbally", help: "Optional context." }),
  ],
  builder_delay: [
    t("project_name", "Project / builder name", { required: true, placeholder: "e.g. Green Meadows by ABC Builders", help: "The project and builder." }),
    t("amount_paid", "Amount paid (₹)", { required: true, placeholder: "e.g. 45,00,000", help: "Total paid so far." }),
    t("promised_possession_date", "Promised possession date", { required: true, placeholder: "e.g. Dec 2023", help: "When possession was promised." }),
    t("unit_details", "Unit details", { placeholder: "e.g. Flat B-702, 2BHK", help: "Optional." }),
    area("delay_details", "Details of the delay", { placeholder: "e.g. 18 months overdue, no clear timeline given", help: "Optional." }),
  ],
  security_deposit: [
    area("property_address", "Property address", { required: true, placeholder: "Full address of the rented property", help: "The property you rented." }),
    t("deposit_amount", "Deposit amount (₹)", { required: true, placeholder: "e.g. 1,00,000", help: "The security deposit to be refunded." }),
    t("vacated_date", "Date you vacated", { required: true, placeholder: "e.g. 30 Apr 2025", help: "When you handed back possession." }),
    area("landlord_response", "Landlord's response so far", { placeholder: "e.g. Ignoring calls / partial deduction claimed", help: "Optional." }),
  ],

  // ---- Cyber & Online Fraud ----
  upi_fraud: [
    t("amount", "Amount lost (₹)", { required: true, placeholder: "e.g. 25,000", help: "How much you lost." }),
    t("transaction_date", "Date of transaction", { required: true, placeholder: "e.g. 3 May 2025", help: "When the fraud happened." }),
    area("how_happened", "What happened?", { required: true, placeholder: "e.g. Received a fake payment link and money was debited", help: "Explain the fraud clearly." }),
    t("upi_txn_id", "Transaction / UTR ID", { placeholder: "e.g. 5091xxxxxx", help: "Optional but helpful." }),
    area("other_party_details", "Who is the other party?", { placeholder: "e.g. UPI ID, phone number or name used", help: "Optional — any detail about the fraudster." }),
  ],
  online_seller_scam: [
    t("platform", "Platform / website", { required: true, placeholder: "e.g. Instagram / a website", help: "Where the scam happened." }),
    t("amount", "Amount lost (₹)", { required: true, placeholder: "e.g. 8,000", help: "How much you paid." }),
    area("what_happened", "What happened?", { required: true, placeholder: "e.g. Paid for a product via Instagram, seller blocked me", help: "Explain the scam." }),
    area("seller_details", "Seller details", { placeholder: "e.g. Handle @xyz, phone, UPI ID", help: "Optional — any detail about the seller." }),
    t("order_ref", "Order / payment reference", { placeholder: "e.g. Txn ref, screenshots", help: "Optional." }),
  ],
  loan_app_harassment: [
    t("app_name", "Loan app name", { required: true, placeholder: "e.g. QuickCash", help: "The app harassing you." }),
    area("harassment_details", "What are they doing?", { required: true, placeholder: "e.g. Calling my contacts, threatening messages, morphed photos", help: "Describe the harassment/blackmail." }),
    t("loan_amount", "Loan amount (if any) (₹)", { placeholder: "e.g. 10,000", help: "Optional." }),
    t("since_when", "Since when", { placeholder: "e.g. Last 2 weeks", help: "Optional." }),
  ],
  investment_scam: [
    t("platform_or_person", "Platform / person", { required: true, placeholder: "e.g. A Telegram trading group", help: "Who took your money." }),
    t("amount", "Amount invested/lost (₹)", { required: true, placeholder: "e.g. 1,00,000", help: "How much you lost." }),
    area("how_happened", "What happened?", { required: true, placeholder: "e.g. Promised 30% returns, can't withdraw now", help: "Explain the scheme and how you were cheated." }),
    t("promised_returns", "Returns promised", { placeholder: "e.g. Double in 3 months", help: "Optional." }),
    t("dates", "Key dates", { placeholder: "e.g. Invested Mar 2025", help: "Optional." }),
  ],

  // ---- Contract & Others ----
  breach_contract: [
    t("contract_type", "Type of agreement", { required: true, placeholder: "e.g. Service agreement, vendor contract", help: "What the agreement was about." }),
    area("breach_description", "How was it breached?", { required: true, placeholder: "Which terms were broken and how", help: "The specific breach." }),
    t("relief_sought", "What do you want?", { required: true, placeholder: "e.g. Perform the contract / compensate ₹2,00,000", help: "The remedy you're demanding." }),
    t("contract_date", "Date of agreement", { placeholder: "e.g. 10 Oct 2024", help: "Optional." }),
    area("loss_suffered", "Loss suffered", { placeholder: "e.g. Lost ₹2,00,000 in penalties", help: "Optional." }),
  ],
  general: [
    t("subject", "Subject of the notice", { required: true, placeholder: "e.g. Cease and desist — unauthorised use of my brand", help: "A short summary of the matter." }),
    area("facts", "The facts", { required: true, placeholder: "Explain what happened, when, and who was involved", help: "The full background." }),
    t("relief_sought", "What are you demanding?", { required: true, placeholder: "e.g. Stop using the name and apologise in writing", help: "What you want the recipient to do." }),
  ],

  // ---- Business, IP & Reputation ----
  defamation: [
    area("statement", "What was said or published?", { required: true, placeholder: "e.g. A Facebook post calling me a fraud", help: "The exact false/defamatory statement." }),
    t("where_published", "Where was it published?", { required: true, placeholder: "e.g. Instagram / WhatsApp group / newspaper", help: "The platform or medium." }),
    t("when", "When", { required: true, placeholder: "e.g. 12 May 2025", help: "Date it was made/published." }),
    area("harm", "How has it harmed you?", { placeholder: "e.g. Lost clients, reputation damage, mental distress", help: "Optional — the impact on you." }),
    t("relief_sought", "What do you want?", { required: true, placeholder: "e.g. Public apology, removal of the post, ₹5,00,000 damages", help: "The remedy you're demanding." }),
  ],
  copyright_infringement: [
    t("work", "Your original work", { required: true, placeholder: "e.g. Blog article / photograph / software code", help: "The copyrighted work being used." }),
    area("how_infringed", "How is it being infringed?", { required: true, placeholder: "e.g. Copied my article word-for-word on their website", help: "Describe the infringement." }),
    t("where", "Where is it appearing?", { required: true, placeholder: "e.g. www.theirsite.com/blog", help: "URL, platform or location." }),
    t("relief_sought", "What do you want?", { required: true, placeholder: "e.g. Take it down, stop using it, pay damages", help: "The remedy you're demanding." }),
  ],
  trademark_infringement: [
    t("mark", "Your brand name / mark", { required: true, placeholder: "e.g. 'ZenoWear'", help: "The trademark being misused." }),
    t("registration_no", "Trademark registration no.", { placeholder: "e.g. 4567890", help: "Optional — if registered." }),
    area("how_infringed", "How is it being infringed?", { required: true, placeholder: "e.g. Selling products under a deceptively similar name", help: "Describe the misuse." }),
    t("relief_sought", "What do you want?", { required: true, placeholder: "e.g. Stop using the mark, rebrand, pay damages", help: "The remedy you're demanding." }),
  ],
  partnership_dispute: [
    t("firm_name", "Firm / business name", { required: true, placeholder: "e.g. Sharma & Associates", help: "The partnership firm involved." }),
    area("dispute", "What is the dispute?", { required: true, placeholder: "e.g. Partner withdrawing funds without consent, no accounts shared", help: "The core disagreement." }),
    t("your_share", "Your stake / claim", { placeholder: "e.g. 50% share / ₹10,00,000 capital", help: "Optional — your interest in the firm." }),
    t("relief_sought", "What do you want?", { required: true, placeholder: "e.g. Render accounts, stop unauthorised acts, settle my share", help: "The remedy you're demanding." }),
  ],
};

export const STEPS = [
  { key: "type", label: "Notice Type" },
  { key: "sender", label: "Your Details" },
  { key: "recipient", label: "Their Details" },
  { key: "claim", label: "The Matter" },
  { key: "review", label: "Review & Draft" },
  { key: "delivery", label: "Delivery & Options" },
  { key: "payment", label: "Payment" },
  { key: "sent", label: "Submitted" },
];

export const STEP_HELP = {
  type: {
    title: "Why choose a notice type?",
    body: "Each situation follows different legal principles. Picking the exact sub-type lets us ask only the questions your notice needs — nothing more, nothing less.",
    faqs: [
      { q: "Not sure which fits?", a: "Pick the closest match, or choose ‘General Legal Notice’ under Contract & Others. You review everything before it's sent." },
      { q: "Can one notice cover two issues?", a: "It's best to keep one notice per matter. You can create additional notices anytime." },
    ],
  },
  sender: {
    title: "Why we need your details",
    body: "A legal notice must clearly identify who is issuing it. These details appear at the top of your notice so the recipient knows exactly who is making the demand.",
    faqs: [
      { q: "Is my information safe?", a: "Yes. Your details are used only to prepare and send your notice. We never share them." },
      { q: "Business or individual?", a: "Choose ‘Business’ if the notice is issued in your company's name, otherwise ‘Individual’." },
    ],
  },
  recipient: {
    title: "Who is the notice addressed to?",
    body: "This is the person or business you have a dispute with. An accurate name and address are essential — a legal notice must reach the correct party to be effective.",
    faqs: [
      { q: "I only have their email.", a: "That's fine to start, but a postal address makes your notice far stronger. Add it if you can." },
      { q: "What if the address is wrong?", a: "An incorrect address can weaken your notice. Double-check spelling and pin code." },
    ],
  },
  claim: {
    title: "Tell us what happened",
    body: "Fields marked with * are required; the rest are optional but make your notice stronger. Write plainly — we turn your answers into precise legal language.",
    faqs: [
      { q: "Do I need legal terms?", a: "No. Just explain what happened in your own words. We handle the legal wording." },
      { q: "What if I'm missing a detail?", a: "Fill what you can — optional fields can be left blank. We'll phrase missing details carefully." },
    ],
  },
  review: {
    title: "Review your draft",
    body: "We draft a complete legal notice from your answers. Read it carefully and edit any line directly, or ask us to rewrite it with changes. You stay in full control.",
    faqs: [
      { q: "Can I change the wording?", a: "Absolutely. Edit any part directly, or use the ‘rewrite with my changes’ box. Your changes are saved automatically." },
      { q: "Is this advocate-vetted?", a: "After payment, a qualified advocate reviews and stamps it before it's sent." },
    ],
  },
  delivery: {
    title: "Choose how it's delivered",
    body: "Every notice is emailed by default. Add WhatsApp or Registered Post for stronger reach and proof of service, or an advocate follow-up call for maximum legal weight. Confirm the recipient's details so it reaches the right party.",
    faqs: [
      { q: "Why add Registered Post?", a: "A registered post receipt is strong legal proof that the recipient was served — valuable if the matter escalates to court." },
      { q: "What does the advocate call do?", a: "Our advocate personally calls the recipient after dispatch. It signals serious intent and often prompts a faster response." },
    ],
  },
  payment: {
    title: "What your fee covers",
    body: "One simple fee. Our advocate personally reviews your notice, adds the firm's approval seal, and sends it — with a copy to you. No account or paperwork needed.",
    faqs: [
      { q: "Any hidden charges?", a: "No. A clear base fee, with any add-ons you pick shown before you pay." },
      { q: "Is paying safe?", a: "Yes. Payments go through a secure gateway and we never see or store your card details." },
    ],
  },
  sent: {
    title: "With our advocate now",
    body: "Your notice has been sent to our advocate for a final check. Once approved and stamped, it goes straight to the recipient — and a copy lands in your inbox.",
    faqs: [
      { q: "How long does review take?", a: "Usually within one business day. We'll email you the moment your notice is stamped and sent." },
      { q: "What if they don't respond?", a: "You can escalate to formal proceedings. Reply to your email and our office will guide you through it." },
    ],
  },
};
